import type {
  CreatedConversation,
  ConversationHistory,
  Conversation,
  Message,
  FileUploadResponse,
} from "./types";
import type { WsMemberSelectedContext } from "src/redux/ai-chat/aiChatTypes";

const WEBHOOK_URL = "https://n8n-api.idh.cloud/webhook/idh-chat-streaming";
const CREATE_CONVERSATION_URL =
  "https://n8n-api.idh.cloud/webhook/idh-argus-conversations";
const ARGUS_CONVERSATIONS_URL =
  "https://n8n-api.idh.cloud/webhook/idh-argus-conversations";
const FILE_UPLOAD_URL = "https://n8n-api.idh.cloud/webhook/file-upload";

export async function fetchWebhookStream(
  chatInput: string,
  sessionId: string,
  jwtToken: string | null | undefined,
  signal: AbortSignal,
  attachedFiles?: string[],
  wsMemberSelectedContext?: WsMemberSelectedContext | null,
  isWorkspaceHostnameCustomized?: boolean,
  predefinedPrompt?: string,
): Promise<ReadableStreamDefaultReader<Uint8Array>> {
  const response = await fetch(WEBHOOK_URL, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(jwtToken ? { Authorization: `Bearer ${jwtToken}` } : {}),
    },
    body: JSON.stringify({
      action: "sendMessage",
      chatInput,
      sessionId,
      currentUrl: window.location.href,
      predefinedPrompt,
      // n8n only reads attachedFiles when it is a non-empty array of strings.
      ...(attachedFiles && attachedFiles.length ? { attachedFiles } : {}),
      ...(wsMemberSelectedContext ? { wsMemberSelectedContext } : {}),
      ...(isWorkspaceHostnameCustomized !== undefined
        ? { isWorkspaceHostnameCustomized }
        : {}),
    }),
    signal,
  });

  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  const reader = response.body?.getReader();
  if (!reader) throw new Error("No response body");
  return reader;
}

// Uploads the staged files as multipart/form-data. The JSON metadata travels in
// the `payload` form field (not as a JSON body); files are sent as `file[]`.
// Returns the uploaded file names to attach to the subsequent chat message, or
// throws on a non-success result so the caller can surface an error.
//
// Filenames are sanitized before upload because the n8n streaming node splits
// attachedFiles on whitespace, causing "File not found" errors for names with spaces.
function sanitizeFileName(name: string): string {
  return name.replace(/\s+/g, "_");
}

export async function uploadFiles(
  files: File[],
  sessionId: string,
  jwtToken: string | null | undefined,
): Promise<string[]> {
  const formData = new FormData();
  formData.append(
    "payload",
    JSON.stringify({ sessionId, action: "uploadFiles" }),
  );
  files.forEach((file) => {
    const sanitized = new File([file], sanitizeFileName(file.name), {
      type: file.type,
    });
    formData.append("file[]", sanitized);
  });

  const response = await fetch(FILE_UPLOAD_URL, {
    method: "POST",
    headers: {
      ...(jwtToken ? { Authorization: `Bearer ${jwtToken}` } : {}),
    },
    body: formData,
  });

  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  const data = (await response.json()) as FileUploadResponse;
  if (data.result !== "success") {
    throw new Error(data.errorMessage || "File upload failed");
  }
  return files.map((file) => sanitizeFileName(file.name));
}

export async function fetchConversation(
  conversationId: number,
  jwtToken: string,
  signal: AbortSignal,
): Promise<Message[]> {
  const response = await fetch(`${WEBHOOK_URL}?sessionId=${conversationId}`, {
    method: "GET",
    signal,
    headers: { Authorization: `Bearer ${jwtToken}` },
  });
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  return response.json();
}

export async function fetchConversationHistory(
  chatwootContactId: string,
  signal: AbortSignal,
  jwtToken: string,
): Promise<Conversation[]> {
  const response = await fetch(
    `${ARGUS_CONVERSATIONS_URL}?chatwootContactId=${encodeURIComponent(chatwootContactId)}`,
    {
      method: "GET",
      signal,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${jwtToken}`,
      },
    },
  );
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  const data = (await response.json()) as ConversationHistory;
  return data.conversations;
}

export async function createConversation(
  chatwootContactId: string,
  jwtToken: string,
  chatInput: string,
): Promise<number> {
  const response = await fetch(CREATE_CONVERSATION_URL, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${jwtToken}`,
    },
    body: JSON.stringify({
      chatwootContactId,
      chatInput,
    }),
  });

  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  const data = (await response.json()) as CreatedConversation;
  return data.conversation.id;
}
